import BookSegmentModal from "@app/components/book-segment-modal";
import ConfirmModal from "@app/components/confirm-modal";
import CustomAsyncSelect from "@app/components/custom-async-select";
import {
  CustomAsyncSelectRef,
  PromiseOptionsCallback,
} from "@app/components/custom-async-select/custom-async-select.component";
import Table, {
  ColumnConfig,
  Order,
} from "@app/components/table/table.component";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  SystemMessage,
} from "@app/constants";
import BookSegmentService from "@app/services/http/book-segment.service";
import { openPortalDialog } from "@app/services/modal.service";
import {
  BookSegment,
  LabelValue,
  Meta,
  RequestParamsBookSegment,
  UpdateBookSegmentRequest,
} from "@app/types";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import { useCallback, useEffect, useRef, useState } from "react";
import { SingleValue } from "react-select";

const columnConfig: ColumnConfig[] = [
  {
    label: "ID",
    dataKey: "id",
    width: "10%",
    tdClassName: "truncate min-w-[180px] max-w-[280px]",
    hasOrder: true,
  },
  {
    label: "Content",
    dataKey: "content",
    width: "90%",
    tdClassName: "truncate min-w-[180px] max-w-[280px]",
    hasOrder: true,
  },
];

function BookSegmentManagement() {
  const bookDropdownRef = useRef<CustomAsyncSelectRef>(null);
  const [bookSegments, setBookSegments] = useState<BookSegment[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParamsBookSegment>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();
  const [bookSelected, setBookSelected] = useState<SingleValue<LabelValue>>();
  const [bookElementSelected, setBookElementSelected] =
    useState<SingleValue<LabelValue>>();

  const [update, forceUpdate] = useForceUpdate();

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    const newParam: RequestParamsBookSegment = {
      ...requestParams,
    };

    if (bookSelected) {
      newParam.bookId = Number(bookSelected.value);
    }

    if (bookElementSelected) {
      newParam.bookElementId = Number(bookElementSelected.value);
    }

    BookSegmentService.getList(newParam, abortController.signal).then((res) => {
      setBookSegments(res.data.items);
      setMeta(res.data.meta);

      if (
        requestParams.pageNum &&
        res.data.meta.totalPage > 0 &&
        res.data.meta.totalPage < requestParams.pageNum
      ) {
        const newRequestParams: RequestParams = {
          ...requestParams,
          pageNum: DEFAULT_PAGE,
        };

        setRequestParams(newRequestParams);
      }
    });
  }, [bookSelected, bookElementSelected, update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleEditClick = (bookSegment: BookSegment) => {
    const modalObs = openPortalDialog(BookSegmentModal, {
      bookSegment,
    });

    modalObs.afterClosed().subscribe((data) => {
      if (data?.bookSegment) {
        const updateBookSegment: UpdateBookSegmentRequest = {
          content: data.bookSegment.content,
        };

        if (data.bookSegment.media) {
          updateBookSegment.media = data.bookSegment.media;
        }

        if (data.bookSegment.mediaLink) {
          updateBookSegment.mediaLink = data.bookSegment.mediaLink;
        }

        BookSegmentService.update(bookSegment.id, updateBookSegment).then(
          () => {
            addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
            forceUpdate();
          }
        );
      }
    });
  };

  const handleDeleteClick = (bookSegmentId: number) => {
    if (bookSegmentId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.DELETE_BOOK_SEGMENT_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          BookSegmentService.delete(bookSegmentId).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  const getBookDropdownOptions = useCallback(
    (inputValue: string, callback: PromiseOptionsCallback) => {
      BookSegmentService.getBookDropdown(
        inputValue.trim(),
        abortController.signal
      ).then((res) => {
        callback(res.data.items);
      });
    },
    []
  );

  const getBookElementDropdownOptions = useCallback(
    (inputValue: string, callback: PromiseOptionsCallback) => {
      if (bookSelected) {
        BookSegmentService.getBookElementDropdown(
          Number(bookSelected.value),
          inputValue.trim(),
          abortController.signal
        ).then((res) => {
          callback(res.data.items);
        });
      } else {
        callback([]);
      }
    },
    [bookSelected]
  );

  const handleBookDropdownChange = (v: SingleValue<LabelValue>) => {
    bookDropdownRef.current?.resetValue();
    setBookSelected(v);
  };

  const handleBookElementDropdownChange = (v: SingleValue<LabelValue>) => {
    setBookElementSelected(v);
  };

  return (
    <div className="py-6 px-12">
      <div className="flex items-center">
        <div className="text-[#343C6A]">Danh sách book segment</div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <div className="flex flex-wrap justify-between items-center">
          <div className="black-60">Danh sách book segment</div>
          <div className="flex items-center gap-5">
            <div className="w-[200px]">
              <CustomAsyncSelect
                promiseOptions={getBookDropdownOptions}
                placeholder="Chọn sách"
                onChange={handleBookDropdownChange}
              />
            </div>
            <div className="w-[220px]">
              <CustomAsyncSelect
                ref={bookDropdownRef}
                _key={bookSelected?.value}
                promiseOptions={getBookElementDropdownOptions}
                placeholder="Chọn chương"
                onChange={handleBookElementDropdownChange}
              />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <div className="w-full flex relative">
                  <input
                    className="w-[180px] outline-none pl-10 px-4 h-9 leading-9 rounded black-60 border border-solid border-[#CFD3D4] text-xs"
                    placeholder="Tìm kiếm"
                    onChange={(event) => onSearchChange(event.target.value)}
                  />
                  <img
                    className="absolute w-4 h-4 left-2 top-[10px]"
                    src={Images.SearchIcon.default}
                    alt=""
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <Table
            columnConfig={columnConfig}
            dataTable={bookSegments}
            hasCheckBox
            hasAction
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default BookSegmentManagement;
